'use client';

import React, { useState, useEffect } from 'react';
import { Navbar, Footer } from '../../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { 
  ShoppingBag, 
  RefreshCw, 
  AlertCircle, 
  Calendar, 
  IndianRupee, 
  Clock, 
  ArrowLeft, 
  ChevronDown, 
  ChevronUp, 
  Download, 
  Undo2, 
  CheckCircle2, 
  Truck, 
  PackageCheck,
  CreditCard
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { jsPDF } from 'jspdf';

const decodeToken = (token: string) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};

interface OrderItem {
  productId: number;
  productName: string;
  variantName: string;
  price: number;
  quantity: number;
  cookingInstructions?: string;
}

interface Order {
  id: number;
  customerId: string;
  totalAmount: number;
  status: 'PENDING' | 'CONFIRMED' | 'SHIPPED' | 'DELIVERED' | 'CANCELLED' | 'RETURN_REQUESTED' | 'RETURNED' | 'REFUNDED';
  shippingFee?: number;
  pincode?: string;
  createdAt?: string;
  items?: OrderItem[];
  billUrl?: string;
  customerPhone?: string;
  shippingAddress?: string;
  deliveryLatitude?: number;
  deliveryLongitude?: number;
}

export default function MyOrdersPage() {
  const { isAuthenticated, token, logout } = useAuth();
  const router = useRouter();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [username, setUsername] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [expandedOrderId, setExpandedOrderId] = useState<number | null>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [storeContact, setStoreContact] = useState<any>({
    address: "123 Organic Lane, Green Valley, Erode, Tamil Nadu, 638001",
    phone: "+91 98765 43210",
    email: "care@organicheaven.com"
  });

  useEffect(() => {
    const authApi = process.env.NEXT_PUBLIC_AUTH_API || 'http://localhost:8080';
    fetch(`${authApi}/api/settings`)
      .then(res => res.json())
      .then(settings => {
        if (settings.footer_contact_details) {
          try {
            const parsed = JSON.parse(settings.footer_contact_details);
            setStoreContact({
              address: parsed.address || "123 Organic Lane, Green Valley, Erode, Tamil Nadu, 638001",
              phone: parsed.phone || "+91 98765 43210",
              email: parsed.email || "care@organicheaven.com"
            });
          } catch (e) {}
        }
      })
      .catch(err => console.warn("Failed to load invoice settings", err));
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      router.push('/login');
      return;
    }
    if (token) {
      const decoded = decodeToken(token);
      if (decoded && decoded.sub) {
        setUsername(decoded.sub);
      }
      
      const authApi = process.env.NEXT_PUBLIC_AUTH_API || 'http://localhost:8080';
      fetch(`${authApi}/api/users/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      .then(res => res.json())
      .then(data => {
        if (data && data.firstName) {
          setCustomerName(`${data.firstName} ${data.lastName}`.trim());
        }
      })
      .catch(() => {});
    }
  }, [isAuthenticated, token, router]);

  const fetchMyOrders = async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const orderApi = process.env.NEXT_PUBLIC_ORDER_API || 'http://localhost:8082';
      const res = await fetch(`${orderApi}/api/orders`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        const decoded = decodeToken(token);
        const currentCust = decoded?.sub || '';
        
        // Filter orders for the logged in customer and sort by newest first
        const filtered = (data || [])
          .filter((ord: Order) => ord.customerId === currentCust)
          .sort((a: Order, b: Order) => b.id - a.id);
        setOrders(filtered);
      } else {
        if (res.status === 401 || res.status === 403) {
          logout();
          return;
        }
        setError('Failed to retrieve order history from order-service.');
      }
    } catch (err) {
      console.error(err);
      setError('Connection failure with order service.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchMyOrders();
    }
  }, [isAuthenticated, token]);

  const handleRequestReturn = async (orderId: number) => {
    if (!confirm('Are you sure you want to request a return for this order?')) return;
    setActionLoading(orderId);
    try {
      const orderApi = process.env.NEXT_PUBLIC_ORDER_API || 'http://localhost:8082';
      const res = await fetch(`${orderApi}/api/orders/${orderId}/return`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (res.ok) {
        // Refresh orders list
        await fetchMyOrders();
      } else {
        if (res.status === 401 || res.status === 403) {
          logout();
          return;
        }
        alert('Failed to request return. Contact support.');
      }
    } catch (err) {
      console.error(err);
      alert('Network error requesting return.');
    } finally {
      setActionLoading(null);
    }
  };

  const sanitizeString = (str: string): string => {
    if (!str) return '';
    return str
      .replace(/[^\x20-\x7E]/g, '') // Keep printable ASCII
      .replace(/\s+/g, ' ')          // Collapse spaces
      .trim();
  };

  const generatePDFInvoice = (order: Order) => {
    const doc = new jsPDF({
      unit: 'mm',
      format: [100, 150]
    });

    // Outer border
    doc.setDrawColor(28, 58, 43);
    doc.setLineWidth(1);
    doc.rect(2, 2, 96, 146);

    // Title centered
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(28, 58, 43);
    doc.text("Invoice", 50, 10, { align: "center" });

    // Draw Logo: Circular emblem
    doc.setFillColor(28, 58, 43);
    doc.circle(10, 17, 5, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.text("PV", 10, 18.5, { align: "center" });

    // Left Column Info (Brand, Address, Contact)
    doc.setTextColor(28, 58, 43);
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.text("Pasumai Virumbi", 17, 18.5);

    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    
    // Split and render dynamic store address to avoid overflows
    const storeAddrSplit = doc.splitTextToSize(sanitizeString(storeContact.address), 44);
    doc.text(storeAddrSplit, 5, 25);
    const storeAddrOffset = storeAddrSplit.length * 3.5;
    doc.text(`Ph: ${sanitizeString(storeContact.phone)}`, 5, 25 + storeAddrOffset);
    doc.text(`Email: ${sanitizeString(storeContact.email)}`, 5, 29 + storeAddrOffset);
    doc.text("Web: www.pasumaivirumbi.com", 5, 33 + storeAddrOffset);

    // Right Column Info (Customer Name, Customer Address, OrderId, Order Date)
    doc.setTextColor(28, 58, 43);
    doc.setFont("helvetica", "bold");
    doc.text("Customer Details:", 52, 17);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    const displayName = sanitizeString(customerName || order.customerId);
    const splitName = doc.splitTextToSize(displayName, 42);
    doc.text(splitName, 52, 21);
    
    let rightOffset = 21 + (splitName.length * 3.5);
    
    if (order.shippingAddress) {
      doc.setFont("helvetica", "bold");
      doc.setTextColor(28, 58, 43);
      doc.text("Shipping Address:", 52, rightOffset);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(80, 80, 80);
      const cleanAddress = sanitizeString(order.shippingAddress);
      const splitAddress = doc.splitTextToSize(cleanAddress, 42);
      doc.text(splitAddress, 52, rightOffset + 3.5);
      rightOffset += 3.5 + (splitAddress.length * 3.5);
    }
    
    doc.setTextColor(28, 58, 43);
    doc.setFont("helvetica", "bold");
    doc.text("Order ID:", 52, rightOffset);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    doc.text(`#${order.id}`, 52, rightOffset + 3.5);
    rightOffset += 7;

    doc.setTextColor(28, 58, 43);
    doc.setFont("helvetica", "bold");
    doc.text("Order Date:", 52, rightOffset);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    const dateStr = order.createdAt ? new Date(order.createdAt).toLocaleDateString() : 'Recent';
    doc.text(dateStr, 52, rightOffset + 3.5);
    rightOffset += 7;

    let nextY = Math.max(38 + storeAddrOffset, rightOffset + 2);
    doc.setDrawColor(200, 200, 200);
    doc.line(5, nextY, 95, nextY);
    nextY += 4;

    // Items list
    const items = order.items || [];
    if (items.length > 0) {
      items.forEach((item: any) => {
        if (nextY > 105) return;
        doc.setFont("helvetica", "bold");
        doc.setTextColor(28, 58, 43);
        const displayVariant = (item.variantName === 'Default' || !item.variantName) ? '500g' : item.variantName;
        const cleanedName = sanitizeString(item.productName || 'Organic Item');
        const itemLine = `${cleanedName} (${sanitizeString(displayVariant)}) x ${item.quantity}`;
        const splitItemLine = doc.splitTextToSize(itemLine, 65);
        doc.text(splitItemLine, 5, nextY);

        doc.setFont("helvetica", "normal");
        doc.setTextColor(80, 80, 80);
        doc.text(`Rs. ${item.price * item.quantity}`, 95, nextY, { align: "right" });
        nextY += splitItemLine.length * 4.5;

        if (item.cookingInstructions) {
          doc.setTextColor(180, 50, 50);
          doc.setFont("helvetica", "bold");
          const splitInst = doc.splitTextToSize(`[*] ${sanitizeString(item.cookingInstructions)}`, 85);
          doc.text(splitInst, 8, nextY);
          nextY += splitInst.length * 4.5;
        }
      });
    }

    // Subtotal section
    nextY = Math.max(nextY, 105);
    doc.setDrawColor(200, 200, 200);
    doc.line(5, nextY, 95, nextY);
    nextY += 4;

    const subtotal = order.totalAmount - (order.shippingFee || 0);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    
    // Subtotal
    doc.text("Subtotal", 60, nextY);
    doc.text(`Rs. ${subtotal}`, 95, nextY, { align: "right" });
    nextY += 4;

    // Shipping
    doc.text("Shipping", 60, nextY);
    doc.text(`Rs. ${order.shippingFee || 0}`, 95, nextY, { align: "right" });
    nextY += 5;

    // Grand Total
    doc.setFont("helvetica", "bold");
    doc.setTextColor(28, 58, 43);
    doc.setFontSize(10);
    doc.text("Grand Total", 5, nextY);
    doc.text(`Rs. ${order.totalAmount}`, 95, nextY, { align: "right" });
    nextY += 6;

    // Thank you & Disclaimer
    nextY = Math.min(nextY, 134);
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8);
    doc.text("Thank you for your order!", 50, nextY, { align: "center" });
    nextY += 4;
    doc.setFontSize(6);
    doc.setTextColor(120, 120, 120);
    doc.text("This is a computer generated invoice. No signature required.", 50, nextY, { align: "center" });

    doc.save(`Invoice_PasumaiVirumbi_Order_${order.id}.pdf`);
  };

  const getStatusBadgeStyle = (status: string) => {
    switch (status) {
      case 'DELIVERED':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'SHIPPED':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'CONFIRMED':
        return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'PENDING':
        return 'bg-amber-50 text-amber-700 border-amber-200 animate-pulse';
      case 'CANCELLED':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'RETURN_REQUESTED':
        return 'bg-yellow-50 text-yellow-800 border-yellow-200 animate-pulse';
      case 'RETURNED':
        return 'bg-orange-50 text-orange-700 border-orange-200';
      case 'REFUNDED':
        return 'bg-[#0f4c3a]/10 text-mid-green border-[#0f4c3a]/20';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getStatusLabel = (status: string) => {
    return status.replace('_', ' ');
  };

  // Milestone indexes for animation
  const statusMilestones = ['PENDING', 'CONFIRMED', 'SHIPPED', 'DELIVERED'];

  return (
    <div className="min-h-screen bg-[#F8F9F5] font-body text-text-primary flex flex-col justify-between">
      <Navbar />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-12 w-full flex-grow text-left">
        {/* Breadcrumb */}
        <div className="mb-6">
          <Link href="/" className="inline-flex items-center gap-2 text-text-muted hover:text-deep-green font-bold text-xs uppercase tracking-wider transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Storefront
          </Link>
        </div>

        {/* Page Header */}
        <div className="flex justify-between items-center mb-8 border-b border-border/60 pb-6">
          <div>
            <h1 className="text-3xl md:text-4xl font-display font-black text-deep-green">My Orders</h1>
            <p className="text-text-muted text-sm font-medium mt-1">Hello <span className="font-bold text-deep-green">{username}</span>, track your organic purchases, request returns, or download invoices.</p>
          </div>
          <button 
            onClick={fetchMyOrders}
            disabled={loading}
            className="p-3 bg-white hover:bg-surface border border-border/80 rounded-xl transition-all shadow-sm active:scale-95"
            title="Refresh Orders"
          >
            <RefreshCw className={`w-4 h-4 text-deep-green ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-xl text-sm font-semibold mb-6 flex items-start gap-3 shadow-sm">
            <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12 sm:py-20 bg-transparent sm:bg-white rounded-none sm:rounded-3xl border-0 sm:border border-border/60 shadow-none sm:shadow-card flex flex-col items-center justify-center">
            <RefreshCw className="w-10 h-10 text-mid-green animate-spin mb-4" />
            <span className="text-text-muted font-bold text-sm">Harvesting your purchase history...</span>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12 sm:py-16 px-0 sm:px-6 bg-transparent sm:bg-white rounded-none sm:rounded-3xl border-0 sm:border border-border/60 shadow-none sm:shadow-card flex flex-col items-center justify-center">
            <div className="w-16 h-16 bg-pale-green/40 rounded-full flex items-center justify-center mb-6 shadow-inner border border-mid-green/10">
              <ShoppingBag className="w-8 h-8 text-mid-green" />
            </div>
            <h3 className="text-xl font-bold text-deep-green mb-2">No orders found</h3>
            <p className="text-text-muted text-sm font-medium mb-8 max-w-sm">It looks like you haven't placed any orders yet. Head back to shop our fresh organic harvests!</p>
            <Link 
              href="/products" 
              className="bg-mid-green hover:bg-deep-green text-white px-8 py-3.5 rounded-xl font-bold text-xs uppercase tracking-wider shadow-lg shadow-mid-green/10 transition-all"
            >
              Start Shopping
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => {
              const isExpanded = expandedOrderId === order.id;
              const currentStepIndex = statusMilestones.indexOf(order.status);
              const isCancelled = order.status === 'CANCELLED';
              const isReturnFlow = ['RETURN_REQUESTED', 'RETURNED', 'REFUNDED'].includes(order.status);

              return (
                <div 
                  key={order.id}
                  className="bg-white rounded-2xl sm:rounded-3xl border border-border/50 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                >
                  {/* Card Main Info Tab */}
                  <div 
                    onClick={() => setExpandedOrderId(isExpanded ? null : order.id)}
                    className="p-4 sm:p-6 md:p-8 cursor-pointer flex flex-col md:flex-row justify-between items-start md:items-center gap-4 transition-colors hover:bg-surface/10"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-pale-green/30 rounded-2xl flex items-center justify-center shrink-0 border border-mid-green/10 text-mid-green">
                        <ShoppingBag className="w-6 h-6" />
                      </div>
                      <div>
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="font-mono font-black text-deep-green text-lg">Order #{order.id}</span>
                          <span className={`inline-flex px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider border ${getStatusBadgeStyle(order.status)}`}>
                            {getStatusLabel(order.status)}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-6 text-xs text-text-muted mt-3 font-semibold">
                          <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4 text-mid-green"/> {order.createdAt ? new Date(order.createdAt).toLocaleDateString() : 'Recent'}</span>
                          {!isCancelled && !isReturnFlow && (
                            <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-mid-green"/> {order.status === 'DELIVERED' ? 'Delivered' : 'Est. Delivery: 1-2 Days'}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-6 self-stretch md:self-auto justify-between border-t md:border-t-0 border-border/60 pt-4 md:pt-0">
                      <div className="flex flex-col text-right">
                        <span className="text-[10px] text-text-muted font-black uppercase tracking-wider mb-0.5 hidden md:block">Total Amount</span>
                        <div className="flex items-center font-bold text-deep-green text-xl leading-none">
                          <IndianRupee className="w-4.5 h-4.5 stroke-[2.5]" />
                          <span>{order.totalAmount.toLocaleString('en-IN')}</span>
                        </div>
                      </div>
                      <div className="text-text-muted hover:text-deep-green p-1 transition-colors">
                        {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </div>
                    </div>
                  </div>

                  {/* Expandable Details Section */}
                  <AnimatePresence initial={false}>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: 'auto' }}
                        exit={{ height: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className="overflow-hidden border-t border-border/40 bg-surface/10"
                      >
                        <div className="p-4 sm:p-6 md:p-8 space-y-8">
                          {/* 1. Animated Tracking Timeline */}
                          {!isCancelled && !isReturnFlow && (
                            <div className="space-y-4">
                              <h4 className="text-xs font-black uppercase text-deep-green tracking-widest text-left">Real-Time Tracking</h4>
                              
                              <div className="relative pt-6 pb-2">
                                {/* Base track bar running through circle centers (18px from top of circles row) */}
                                <div className="absolute top-[42px] left-[18px] right-[18px] h-1 bg-border -translate-y-1/2 -z-10 rounded-full"></div>
                                {/* Animated active progress bar */}
                                <motion.div 
                                  className="absolute top-[42px] left-[18px] right-[18px] h-1 bg-mid-green -translate-y-1/2 -z-10 rounded-full"
                                  initial={{ width: 0 }}
                                  animate={{ width: `${(Math.max(0, currentStepIndex) / (statusMilestones.length - 1)) * 100}%` }}
                                  transition={{ duration: 0.8, ease: 'easeOut' }}
                                  style={{ transformOrigin: 'left center' }}
                                />

                                <div className="flex justify-between items-start relative">
                                  {statusMilestones.map((milestone, idx) => {
                                    const isActive = idx <= currentStepIndex;
                                    const isCurrent = idx === currentStepIndex;
                                    
                                    let IconComponent = CheckCircle2;
                                    if (milestone === 'SHIPPED') IconComponent = Truck;
                                    if (milestone === 'DELIVERED') IconComponent = PackageCheck;

                                    return (
                                      <div key={milestone} className="flex flex-col items-center gap-2 w-20">
                                        <motion.div 
                                          className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors border shadow-sm ${
                                            isActive 
                                              ? 'bg-mid-green border-mid-green text-white shadow-mid-green/20' 
                                              : 'bg-white border-border text-text-muted'
                                          }`}
                                          initial={false}
                                          animate={isCurrent ? { scale: [1, 1.12, 1] } : {}}
                                          transition={{ repeat: isCurrent ? Infinity : 0, duration: 2 }}
                                        >
                                          <IconComponent className="w-4 h-4" />
                                        </motion.div>
                                        <span className={`text-[9px] font-black uppercase tracking-wider text-center ${
                                          isCurrent ? 'text-deep-green' : isActive ? 'text-mid-green' : 'text-text-muted'
                                        }`}>
                                          {milestone}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Cancellations Alert */}
                          {isCancelled && (
                            <div className="bg-red-50 border border-red-200 p-4 rounded-2xl flex items-center gap-3 text-red-700 text-xs font-semibold">
                              <AlertCircle className="w-5 h-5 shrink-0" />
                              This order has been cancelled. No shipping tracking is available.
                            </div>
                          )}

                          {/* Returns Workflow Display */}
                          {isReturnFlow && (
                            <div className="space-y-4">
                              <h4 className="text-xs font-black uppercase text-deep-green tracking-widest text-left">Return Status</h4>
                              <div className="p-5 rounded-2xl border border-yellow-250 bg-yellow-50/40 space-y-2">
                                <p className="text-xs font-bold text-yellow-800 flex items-center gap-2">
                                  <Undo2 className="w-4 h-4 animate-pulse" />
                                  Current Return Status: {order.status.replace('_', ' ')}
                                </p>
                                <p className="text-[11px] text-text-muted font-medium">
                                  {order.status === 'RETURN_REQUESTED' && 'Your request has been filed. Our farm representatives will inspect the products and authorize the refund shortly.'}
                                  {order.status === 'RETURNED' && 'Returned items picked up successfully. Initiating wallet/account refund.'}
                                  {order.status === 'REFUNDED' && 'Refunded fully! The invoice is updated and refund has been credited.'}
                                </p>
                              </div>
                            </div>
                          )}

                          {/* 2. Order Line Items Table */}
                          <div className="space-y-3">
                             <h4 className="text-xs font-black uppercase text-deep-green tracking-widest text-left">Item Details</h4>
                             <div className="bg-white border border-border/40 rounded-2xl divide-y divide-border/40 overflow-hidden shadow-sm">
                               {order.items && order.items.length > 0 ? (
                                 order.items.map((item, idx) => (
                                   <div key={idx} className="p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-4 text-sm font-semibold">
                                     <div className="min-w-0 flex-grow">
                                       <h5 className="text-deep-green truncate">{item.productName || 'Organic Product'}</h5>
                                       {item.cookingInstructions && (
                                         <div className="mt-1 p-2 bg-red-50 text-red-700 text-xs rounded-lg border border-red-100 font-bold max-w-fit">
                                           Cut Preference: {item.cookingInstructions}
                                         </div>
                                       )}
                                     </div>
                                     <div className="text-xs text-text-muted font-bold px-2.5 py-1 bg-surface border border-border/50 rounded-lg">
                                       {item.variantName === 'Default' || !item.variantName ? '500g' : item.variantName} × {item.quantity}
                                     </div>
                                     <span className="text-amber shrink-0 min-w-[70px] text-right">₹{item.price * item.quantity}</span>
                                   </div>
                                 ))
                               ) : (
                                 <div className="p-4 text-xs font-medium text-text-muted">
                                   Packages details not stored locally. Refer to your PDF invoice for variant breakdowns.
                                 </div>
                               )}
                             </div>
                          </div>

                          {/* 3. Shipping address, fees, and invoice print actions */}
                          <div className="flex flex-col md:flex-row justify-between items-stretch gap-6 border-t border-border/40 pt-6">
                            <div className="text-xs space-y-1 font-semibold text-text-muted">
                              <p className="text-deep-green font-bold uppercase tracking-wider text-[10px]">Shipping Details</p>
                              {order.shippingAddress && (
                                <p>Address: <span className="text-deep-green font-bold">{(order.shippingAddress.split('|')[0] || '').trim()}</span></p>
                              )}
                              <p>Shipping Pincode: <span className="text-deep-green font-bold">{order.pincode || 'N/A'}</span></p>
                              <p>Estimated Delivery Surcharge: <span className="text-deep-green font-bold">₹{order.shippingFee || 0}</span></p>
                            </div>
                            
                            <div className="flex gap-3 flex-wrap">
                              {/* Invoice Generation Download Link */}
                              <button
                                onClick={() => generatePDFInvoice(order)}
                                className="px-4 py-2.5 bg-deep-green text-white hover:bg-mid-green font-bold text-xs uppercase tracking-wider rounded-xl shadow-md transition-all flex items-center gap-1.5"
                              >
                                <Download className="w-4 h-4" /> Download PDF Bill
                              </button>

                              {/* Request Return Action (Available if status is DELIVERED) */}
                              {order.status === 'DELIVERED' && (
                                <button
                                  onClick={() => handleRequestReturn(order.id)}
                                  disabled={actionLoading === order.id}
                                  className="px-4 py-2.5 bg-yellow-500 hover:bg-yellow-600 text-white font-bold text-xs uppercase tracking-wider rounded-xl shadow-md transition-all flex items-center gap-1.5 disabled:opacity-50"
                                >
                                  {actionLoading === order.id ? (
                                    <>
                                      <RefreshCw className="w-4 h-4 animate-spin" /> Submitting...
                                    </>
                                  ) : (
                                    <>
                                      <Undo2 className="w-4 h-4" /> Request Return
                                    </>
                                  )}
                                </button>
                              )}
                            </div>
                          </div>

                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
